import { RefCallback, useCallback, useEffect, useState } from 'react'

import { BoxModalContextValue } from '../types'

type UsePrefetchInViewportProps = {
  context: BoxModalContextValue
}

export function usePrefetchOnView({ context }: UsePrefetchInViewportProps) {
  const { enablePrefetch, initialized } = context
  const [observer, setObserver] = useState<IntersectionObserver | null>(null)
  useEffect(() => {
    if (initialized && global.IntersectionObserver) {
      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (
            entries.some(({ boundingClientRect, isIntersecting, intersectionRatio }) => {
              const isIntersectingNow =
                isIntersecting && boundingClientRect.height > 0 && boundingClientRect.width > 0
              if (!isIntersectingNow) {
                return false
              }

              // this way we can wait until the highest threshold triggers for this element, if
              // nothing else is available it will trigger the prefetch now
              const canIntersectLater = intersectionObserver.thresholds.some(
                (threshold) =>
                  threshold > intersectionRatio &&
                  innerHeight >= boundingClientRect.height * threshold
              )
              return !canIntersectLater
            })
          ) {
            enablePrefetch()
          }
        },
        { threshold: [0, 0.1, 0.25] }
      )
      setObserver(intersectionObserver)

      return () => {
        intersectionObserver.disconnect()
        setObserver(null)
      }
    }
  }, [enablePrefetch, initialized])

  const prefetchOnViewRef: RefCallback<HTMLElement> = useCallback(
    (element) => {
      if (observer && element) {
        observer.observe(element)
      }
    },
    [observer]
  )

  return { prefetchOnViewRef }
}
